/**
 * Every tracking parameter name is written here and nowhere else.
 *
 * Before this file the same concept travelled under different names depending
 * on which component fired it (arrival_date vs check_in_date, total_value vs
 * value) and room_type meant a Navarino code in one event and a display name
 * in another. Funnelling every payload through these builders is what stops
 * that drifting apart again.
 *
 * Conventions, fixed:
 *   room_type   always the Navarino code (stay.roomType, room.bookingId)
 *   room_name   always the display name  (stay.roomTypeName, room.name)
 *   value       always a number in CURRENCY, never a string, never per-night
 *   dates       always YYYY-MM-DD
 *
 * Pure functions over plain objects. No store, no window, no vendor.
 */

import moment from 'moment';

import { CURRENCY } from '../../constants/analytics';


// GA4 and Meta both treat a null as a value; an absent key is what we want.
const omitEmpty = (params) => Object.keys(params).reduce((acc, key) => {
  if (params[key] !== undefined && params[key] !== null) acc[key] = params[key];
  return acc;
}, {});

const toNumber = (value) => {
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Guards the Infinity that a zero-quantity add-on would otherwise produce.
const unitPrice = (total, quantity) => (quantity > 0 ? toNumber(total) / quantity : toNumber(total));

export const departureFrom = (arrivalDate, duration) => (
  arrivalDate && duration ? moment(arrivalDate).add(duration, 'days').format('YYYY-MM-DD') : undefined
);


// Availability search, both entrances.
export const searchParams = ({
  arrivalDate, departureDate, nights, numAdults, numChildren,
}) => omitEmpty({
  check_in_date: arrivalDate,
  check_out_date: departureDate,
  nights,
  num_adults: numAdults,
  num_children: numChildren,
});

// A room seen, before any rate is chosen. `value` is the from-price.
export const roomParams = ({ roomType, roomName, value }) => omitEmpty({
  room_type: roomType,
  room_name: roomName,
  value: value === undefined ? undefined : toNumber(value),
  currency: value === undefined ? undefined : CURRENCY,
});

// A specific rate on a specific room, as shown in <Rate>.
export const rateParams = ({ roomType, roomName, stay }) => omitEmpty({
  room_type: roomType,
  room_name: roomName || (stay && stay.roomTypeName),
  rate_plan: stay && stay.ratePlan,
  rate_plan_name: stay && stay.ratePlanName,
  nights: stay && stay.duration,
  value: stay && toNumber(stay.total),
  currency: stay ? CURRENCY : undefined,
});

// The stay held in the store, used by the shared /book/* checkout steps.
export const stayParams = (stay) => (stay ? omitEmpty({
  // Set on the stay at SELECT_STAY, which is where both entrances converge.
  source: stay.source,
  room_type: stay.roomType,
  room_name: stay.roomTypeName,
  rate_plan: stay.ratePlan,
  rate_plan_name: stay.ratePlanName,
  check_in_date: stay.arrivalDate,
  check_out_date: stay.departureDate || departureFrom(stay.arrivalDate, stay.duration),
  nights: stay.duration,
  num_adults: stay.adults,
  num_children: stay.children,
  num_rooms: stay.numRooms,
  value: toNumber(stay.total),
  currency: CURRENCY,
}) : {});

export const addOnParams = (service) => omitEmpty({
  service_code: service.code,
  service_name: service.name,
  quantity: service.quantity,
  value: toNumber(service.total),
  currency: CURRENCY,
});


/**
 * The single place a booking's value is computed.
 *
 * KNOWN OPEN QUESTION: this adds the add-on totals to the reservation total,
 * which is only right if the reservation total excludes services. The two
 * response parsers disagree about what that field means (parseReservation
 * reads reservation.total, parseBooking reads roomStay.total.amountAfterTax)
 * and the test fixture has no services, so neither settles it. Today's
 * behaviour is preserved deliberately so the hotel's numbers do not shift
 * mid-refactor, and it lives in this one function so correcting it is a
 * one-line change. The server-side totals canary reports the answer on the
 * first real booking with a paid extra.
 */
export const buildPurchase = (reservation, services = [], eventId) => {
  const { stay } = reservation;
  const addOns = (services || []).map((service) => ({
    code: service.code,
    name: service.name,
    quantity: service.quantity,
    total: toNumber(service.total),
  }));
  const roomTotal = toNumber(stay.total);
  const addOnTotal = addOns.reduce((sum, addOn) => sum + addOn.total, 0);

  return {
    transactionId: reservation.confirmationId,
    value: roomTotal + addOnTotal,
    currency: stay.currency || CURRENCY,
    roomTotal,
    addOns,
    roomType: stay.roomType,
    roomName: stay.roomTypeName,
    ratePlan: stay.ratePlan,
    ratePlanName: stay.ratePlanName,
    checkInDate: stay.arrivalDate,
    checkOutDate: departureFrom(stay.arrivalDate, stay.duration),
    nights: stay.duration,
    numItems: addOns.length + 1,
    eventId,
  };
};

/**
 * What a cancellation reverses: the room total plus every add-on, by the same
 * arithmetic as buildPurchase, so refund and purchase agree for a transaction.
 * The retrieved reservation carries the room total as the booking engine's
 * string and its services in the engine's own shape, so the caller passes the
 * add-on totals it has already priced.
 */
export const refundValue = (roomTotal, addOnTotals = []) => (
  toNumber(roomTotal) + (addOnTotals || []).reduce((sum, total) => sum + toNumber(total), 0)
);

// GA4 ecommerce items. item_id is the room/service code so the same SKU is
// recognisable across view_item, add_to_cart and purchase.
export const purchaseItems = (purchase) => [
  {
    item_id: purchase.roomType,
    item_name: purchase.roomName,
    item_category: 'Room',
    item_variant: `${purchase.roomType}:${purchase.ratePlan}`,
    price: unitPrice(purchase.roomTotal, purchase.nights),
    quantity: purchase.nights,
  },
  ...purchase.addOns.map((addOn) => ({
    item_id: addOn.code,
    item_name: addOn.name,
    item_category: 'Add On',
    item_variant: addOn.code,
    price: unitPrice(addOn.total, addOn.quantity),
    quantity: addOn.quantity,
  })),
];

export const purchaseParams = (purchase) => omitEmpty({
  transaction_id: purchase.transactionId,
  value: purchase.value,
  currency: purchase.currency,
  room_type: purchase.roomType,
  room_name: purchase.roomName,
  rate_plan: purchase.ratePlan,
  check_in_date: purchase.checkInDate,
  check_out_date: purchase.checkOutDate,
  nights: purchase.nights,
  items: purchaseItems(purchase),
});

// Meta wants stable product ids, not a confirmation number. Using the room and
// service codes means ViewContent, AddToCart and Purchase all describe the
// same catalogue entries, which is what its optimisation actually needs.
export const purchaseContents = (purchase) => ({
  content_type: 'product',
  content_ids: [purchase.roomType, ...purchase.addOns.map((addOn) => addOn.code)],
  contents: [
    { id: purchase.roomType, quantity: purchase.nights },
    ...purchase.addOns.map((addOn) => ({ id: addOn.code, quantity: addOn.quantity })),
  ],
  num_items: purchase.numItems,
  value: purchase.value,
  currency: purchase.currency,
});
